import { basicValidation, EmployeeToken, EmployeeTokenPayload, ExpiringTokenPayload, fieldValidation, PermissionGroupIds, RefreshToken, RefreshTokenPayload, TokenReadResult, UserToken, UserTokenPayload } from "../..";

export const tokenHelper = {
    writeSub,
    readSub,
    generateToken,
    verifyTokenPayload,
    parseUserToken,
    parseRefreshToken,
    parseEmployeeToken,
    parseAccessToken
};

function writeSub(siteId:string, userId:string)
{
    return `${userId}@${siteId}`;
}

function readSub(sub:string)
{
    try {
        return {
            siteId: sub.substring(sub.indexOf('@')+1),
            userId: sub.substring(0, sub.indexOf('@'))
        }
    }
    catch(_){
        return { siteId:'', userId:'' };
    }
}

// Adds iat and exp fields to the generated token
function generateToken(
    tokenPayload:any,
    expiryInSeconds:number,
    encoder:(token:ExpiringTokenPayload)=>string
){
    const iat = Date.now();
    const exp = iat + (expiryInSeconds * 1000);
    const expiringPayload:ExpiringTokenPayload = {
        ...tokenPayload,
        iat,
        exp
    };

    return encoder(expiringPayload);
}

function verifyTokenPayload(
    payload:ExpiringTokenPayload,
    lifeTimeSeconds?:number,
):TokenReadResult<ExpiringTokenPayload>
{
    if(!payload)
        return { status: 'no-token' };

    try{
        if(payload === null || payload === undefined)
            return { status: 'invalid', errorMessage: 'decoder returned empty' };

        if(!payload.iat) return { status: 'invalid', errorMessage: 'invalid issuedAt' };
        if(!payload.exp) return { status: 'invalid', errorMessage: 'invalid expiresAt' };

        let expires = payload.exp;
        if(lifeTimeSeconds !== undefined){
            const nowPlusLifetime = payload.iat + lifeTimeSeconds*1000;
            expires = Math.min(expires, nowPlusLifetime);
        }

        if(expires < Date.now())
            return { status: 'expired', token:payload };
            
        return { status:'valid', token:payload };
    }
    catch(e:any){
        return { status: 'invalid', errorMessage:'error thrown verifying token payload:' + e?.message || 'no message defined' };
    }
}


function parseUserToken(expiringToken:ExpiringTokenPayload):
    { status: 'success', token:UserToken } |
    { status: 'failure', errorMessage:string }
{
    try{

        const payload = expiringToken as UserTokenPayload;
        
        const token:UserToken = {
            type:'user',
            ...tokenHelper.readSub(payload?.sub),
            email: payload.email,
            firstname: payload.firstname,
            lastname: payload.lastname,
            permissionGroupId: payload.permissionGroupId,
            individualPermissions: payload.individualPermissions || [],
            features:payload.features,
            partner:payload.partner,
            issuedAt: new Date(payload.iat),
            expiresAt: new Date(payload.exp)
        }

        if(!fieldValidation.site.id(token.siteId)) return { status: 'failure', errorMessage: 'invalid siteId' };
        if(!basicValidation.resourceId.isValid(token.userId)) return { status: 'failure', errorMessage: 'invalid userId' };
        if(!basicValidation.isValidEmail(token.email)) return { status: 'failure', errorMessage: 'invalid email' };
        if(!token.firstname || !token.lastname)  return { status: 'failure', errorMessage: 'invalid name' };
        if(PermissionGroupIds.find(p => p===token.permissionGroupId)===undefined) return { status: 'failure', errorMessage: 'invalid permissionGroupId' };
        if(!Array.isArray(token.individualPermissions)) return { status: 'failure', errorMessage: 'invalid individualPermissions' };
        if(token.individualPermissions.some(p => !fieldValidation.user.individualPermission(p)))
            return { status: 'failure', errorMessage: 'invalid individualPermissions' };
            
        return { status:'success', token };
    }
    catch(e:any){
        return { status: 'failure', errorMessage: e?.message || 'unable to parse token user payload' };
    }
}

function parseRefreshToken(expiringToken:ExpiringTokenPayload):
    { status: 'success', token:RefreshToken } |
    { status: 'failure', errorMessage:string }
{
    try{
        const payload = expiringToken as RefreshTokenPayload;
        
        const token:RefreshToken = {
            ...tokenHelper.readSub(payload?.sub),
            issuedAt: new Date(payload.iat),
            expiresAt: new Date(payload.exp)
        }

        if(!fieldValidation.site.id(token.siteId)) return { status: 'failure', errorMessage: 'invalid siteId' };
        if(!basicValidation.resourceId.isValid(token.userId)) return { status: 'failure', errorMessage: 'invalid userId' };
            
        return { status:'success', token };
    }
    catch(e:any){
        return { status: 'failure', errorMessage: e?.message || 'no message defined' };
    }
}

function parseEmployeeToken(expiringToken:ExpiringTokenPayload):
    { status: 'success', token:EmployeeToken } |
    { status: 'failure', errorMessage:string }
{
    try{

        const payload = expiringToken as EmployeeTokenPayload;
        
        const token:EmployeeToken = {
            type:'employee',
            siteId: payload.sub,
            email: payload.email,
            partner:payload.partner,
            issuedAt: new Date(payload.iat),
            expiresAt: new Date(payload.exp)
        }

        if(!fieldValidation.site.id(token.siteId)) return { status: 'failure', errorMessage: 'invalid siteId' };
        if(token.email !== '' && !basicValidation.isValidEmail(token.email)) return { status: 'failure', errorMessage: 'invalid email' };
            
        return { status:'success', token };
    }
    catch(e:any){
        return { status: 'failure', errorMessage: e?.message || 'unable to parse token user payload' };
    }
}

function parseAccessToken(expiringToken:ExpiringTokenPayload):
    { status: 'success', token:UserToken | EmployeeToken } |
    { status: 'failure', errorMessage:string }
{
    if('type' in expiringToken)
    {
        if(expiringToken.type === 'user')
            return parseUserToken(expiringToken);
        else if(expiringToken.type === 'employee')
            return parseEmployeeToken(expiringToken);
        else
            return { status: 'failure', errorMessage: 'invalid token type' };
    }

    return { status: 'failure', errorMessage: 'token type not defined' };
}